import React, { createContext, useContext, useEffect, useState } from "react";
import { loginRequest } from "../api/authApi";
import { ALLOWED_EMAILS, TOKEN_KEY, USER_KEY } from "../api/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, [token]);

  useEffect(() => {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  }, [user]);

  async function login(email, password) {
    setError("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!ALLOWED_EMAILS.map((e) => e.toLowerCase()).includes(normalizedEmail)) {
      const msg = "This email is not authorized to access the admin dashboard.";
      setError(msg);
      throw new Error(msg);
    }

    setLoading(true);
    try {
      const { token: newToken, user: newUser } = await loginRequest(
        normalizedEmail,
        password
      );

      // Defense-in-depth: also verify whatever email the backend echoes back.
      const returnedEmail = (newUser?.email || normalizedEmail).toLowerCase();
      if (!ALLOWED_EMAILS.map((e) => e.toLowerCase()).includes(returnedEmail)) {
        throw new Error("This account is not authorized to access the admin dashboard.");
      }

      setToken(newToken);
      setUser(newUser);
      return newUser;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Login failed. Please check your credentials.";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setToken(null);
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{ user, token, isAuthenticated: !!token, loading, error, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
